import * as React from 'react';
import { useIntl } from 'react-intl';
import CmdBox from '@commander-services/cmd-box';
import dayjs from 'dayjs';
import { IPositionData, IPositionInGraph } from '.';

interface IPositionDetailProps {
  data: IPositionData | null;
  position: IPositionInGraph | null;
  handleLeftArrow: () => void;
  handleRightArrow: () => void;
  leftArrowTitle: string;
  rightArrowTitle: string;
}

export default function PositionDetail(props: IPositionDetailProps): JSX.Element {
  const { formatMessage: f } = useIntl();

  const getResult = (value: number) => {
    if (value === 1) {
      return f({ id: 'confirm.dialog.answerYes' });
    }
    return f({ id: 'confirm.dialog.answerNo' });
  };

  const getResultForElectricState = (value: number) => {
    if (value === 1) {
      return f({ id: 'graph.position.section.body.works' });
    }
    return f({ id: 'graph.position.section.body.doesntwork' });
  };

  const getTdStyle = () => {
    return { border: 0, height: 20, paddingLeft: 28 };
  };

  const getDate = () => {
    if (props.position?.timestamp) {
      return dayjs(props.position.timestamp).format('DD.MM.YYYY HH:mm');
    }
    return '';
  };

  const getAddress = () => {
    if (props.position?.address) {
      return props.position.address;
    }
    return `${props.position?.lat || ''}, ${props.position?.lng || ''}`;
  };

  return (
    <CmdBox
      id="positionDetail"
      title={`${f({ id: 'graph.position.section.title' })} ${
        props.position ? `${getDate()} - ${getAddress()}` : ''
      }`}
      leftArrowTitle={props.leftArrowTitle}
      rightArrowTitle={props.rightArrowTitle}
      handleLeftArrow={props.handleLeftArrow}
      handleRightArrow={props.handleRightArrow}
    >
      {props.data && (
        <table
          style={{ border: 0 }}
          className="table table-striped table-bordered table-listings m-0"
        >
          <tbody>
            {props.data.speed !== null && props.data.speed !== undefined && (
              <tr key="assignment-item-speed">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `graph.velocity.page.title` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.speed} km/h
                </td>
              </tr>
            )}
            {props.data.batteryVoltage !== null && props.data.batteryVoltage !== undefined && (
              <tr key="assignment-item-batteryVoltage">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `alarms.modal.rule.battery_voltage.customLabel` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.batteryVoltage} V
                </td>
              </tr>
            )}
            {props.data.rpm !== null && props.data.rpm !== undefined && (
              <tr key="assignment-item-rpm">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `alarms.modal.rule.engine_rpm.customLabel` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.rpm} ot/m
                </td>
              </tr>
            )}
            {props.data.fuelLevelPercentage !== null &&
              props.data.fuelLevelPercentage !== undefined && (
                <tr key="assignment-item-fuelLevelPercentage">
                  <td style={getTdStyle()} className="align-middle text-left">
                    {f({ id: `enum.alarms.inputDefinitions.inputs.fuel_level_perc` })}
                  </td>
                  <td style={getTdStyle()} className="align-middle text-right  ">
                    {props.data.fuelLevelPercentage} %
                  </td>
                </tr>
              )}
            {props.data.fuelLevelLiters !== null && props.data.fuelLevelLiters !== undefined && (
              <tr key="assignment-item-fuelLevelLiters">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `enum.alarms.inputDefinitions.inputs.fuel_level_liters` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.fuelLevelLiters} L
                </td>
              </tr>
            )}
            {props.data.throttle !== null && props.data.throttle !== undefined && (
              <tr key="assignment-item-throttle">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `enum.alarms.inputDefinitions.inputs.throttle_position` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.throttle} %
                </td>
              </tr>
            )}
            {props.data.temperature !== null && props.data.temperature !== undefined && (
              <tr key="assignment-item-temperature">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `enum.alarms.notificationMessageTags.engine_temperature` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.temperature}
                </td>
              </tr>
            )}
            {props.data.cng !== null && props.data.cng !== undefined && (
              <tr key="assignment-item-cng">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `widget.cng.tooltip.yes` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {getResult(props.data.cng)}
                </td>
              </tr>
            )}
            {props.data.lpg !== null && props.data.lpg !== undefined && (
              <tr key="assignment-item-lpg">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `widget.lpg.tooltip.yes` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {getResult(props.data.lpg)}
                </td>
              </tr>
            )}
            {props.data.cruise !== null && props.data.cruise !== undefined && (
              <tr key="assignment-item-cruise">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `enum.onlineMap.widget.types.cruiseOn` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {getResult(props.data.cruise)}
                </td>
              </tr>
            )}
            {props.data.tachometer !== null && props.data.tachometer !== undefined && (
              <tr key="assignment-item-tachometer">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `form.inputs.mileage` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.tachometer} km
                </td>
              </tr>
            )}
            {props.data.electricBattery !== null && props.data.electricBattery !== undefined && (
              <tr key="assignment-item-electricBattery">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `enum.onlineMap.widget.types.evBattery` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.electricBattery} %
                </td>
              </tr>
            )}
            {props.data.electricRemaining !== null &&
              props.data.electricRemaining !== undefined && (
                <tr key="assignment-item-electricRemaining">
                  <td style={getTdStyle()} className="align-middle text-left">
                    {f({ id: `enum.onlineMap.widget.types.evRange` })}
                  </td>
                  <td style={getTdStyle()} className="align-middle text-right  ">
                    {props.data.electricRemaining / 1000} km
                  </td>
                </tr>
              )}
            {props.data.electricCharge !== null && props.data.electricCharge !== undefined && (
              <tr key="assignment-item-electricCharge">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `enum.onlineMap.widget.types.evCharging` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {getResult(props.data.electricCharge)}
                </td>
              </tr>
            )}
            {props.data.electricState !== null && props.data.electricState !== undefined && (
              <tr key="assignment-item-electricState">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `enum.onlineMap.widget.types.evState` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {getResultForElectricState(props.data.electricState)}
                </td>
              </tr>
            )}
            {props.data.sat !== null && props.data.sat !== undefined && (
              <tr key="assignment-item-sat">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `alarms.modal.rule.satellites_count.customLabel` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.sat}
                </td>
              </tr>
            )}
            {props.data.ecoDrive !== null && props.data.ecoDrive !== undefined && (
              <tr key="assignment-item-ecoDrive">
                <td style={getTdStyle()} className="align-middle text-left">
                  {f({ id: `form.inputs.vehicles.edEnabled` })}
                </td>
                <td style={getTdStyle()} className="align-middle text-right  ">
                  {props.data.ecoDrive}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </CmdBox>
  );
}
